import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import classnames from 'classnames';
import loadable from '@loadable/component';
import { listingPropTypes } from '../../utils/commonPropTypes';
import DetailsContact from '../../pages/Details/DetailsContact';
import messages from '../../utils/translationMessages';
import { Ad } from '@dmm/react-common-components';
import get from 'lodash/get';
import { getConfig } from '../../config/portal';
import './styles.css';
import { CarouselModalDesktop } from './CarouselModal.desktop.js';
import { MediaCarouselModal } from '@dmm/lib-react-ui-components';
import { isBoatDetailsPageRedesign } from '../../utils/newBDPHelper';
import { PortalConfigContext } from '../../config/portal';

const config = getConfig();

const CarouselDisplayer = loadable(() => import('../CarouselDisplayer'));
const CarouselModalHeader = loadable(() => import('../CarouselModalHeader'));

const Modal = ({ handleClose, show, children, formatMessage, device }) => {
  const context = useContext(PortalConfigContext);
  if (isBoatDetailsPageRedesign(context)) {
    const handleCloseModal = () => {
      handleClose();
    };

    return (
      <MediaCarouselModal
        open={show}
        onClose={handleCloseModal}
        showCloseText={device === 'desktop'}
        closeText={formatMessage(messages.carouselModal.close)}
      >
        <div
          className={classnames(
            'modal-mobile',
            { 'modal display-block modal-carousel': show },
            { 'modal display-none': !show }
          )}
        >
          <section className="modal-main-carousel">{children}</section>
        </div>
      </MediaCarouselModal>
    );
  }

  return (
    <div
      className={classnames(
        'modal-mobile',
        { 'modal display-block modal-carousel': show },
        { 'modal display-none': !show }
      )}
    >
      <section className="modal-main-carousel">
        {children}
        <button className="close-carousel-modal" onClick={handleClose}>
          <span className="visuallyhidden">
            {formatMessage(messages.carouselModal.close)}
          </span>
        </button>
      </section>
    </div>
  );
};

Modal.propTypes = {
  /** The object of the transladeable lenguages */
  formatMessage: PropTypes.func.isRequired,
  /** function that will be called on close modal */
  handleClose: PropTypes.func,
  /** show modal book */
  show: PropTypes.bool,
  /** element that will be showed in the midel of the modal */
  children: PropTypes.element,
  device: PropTypes.oneOf(['mobile', 'tablet', 'desktop'])
};

const CarouselModalMobile = (props) => {
  const {
    device,
    isLoading,
    listing,
    selectedCarouselItem,
    adParams,
    adReload,
    intl,
    isManufacturer,
    onCarouselContactButton
  } = props;

  const [isContactModalOpen, toggleContactModal] = useState(false);

  const { locale: currentLocale, formatMessage } = intl;

  const currencyCode = get(
    config,
    `languages.${currentLocale}.currency.abbr`,
    null
  );

  const customPricingOptions = {
    currency: get(config, `supportedCurrencies.${currencyCode}`, undefined),
    formatingOptions: get(
      config,
      `languages.${currentLocale}.currency.format`,
      undefined
    )
  };

  return (
    <Modal
      show={props.show}
      handleClose={props.hideModal}
      formatMessage={formatMessage}
      device={device}
    >
      <>
        {props.show && (
          <>
            <CarouselModalHeader
              listing={listing}
              isLoading={isLoading}
              device={device}
              customPricingOptions={customPricingOptions}
              isManufacturer={isManufacturer}
            />
            <CarouselDisplayer
              isModal={true}
              showThumbs={true}
              device={device}
              isLoading={isLoading}
              listing={listing}
              selectedItem={selectedCarouselItem}
            />
            {device !== 'desktop' && (
              <DetailsContact
                id="carousel-contact-modal-mobile"
                isLoading={isLoading}
                listing={listing}
                disableSellerInfo={true}
                isModal={true}
                toggle={isContactModalOpen}
                showManufacturerForm={isManufacturer}
                onClose={() => toggleContactModal(false)}
                onCarouselContactButton={onCarouselContactButton}
              />
            )}
            {device === 'mobile' && (
              <Ad
                reloadAd={adReload}
                targeting={{ refresh: adReload.toString() }}
                {...adParams.mobileLeaderboardParams}
              />
            )}
            {device !== 'mobile' && (
              <Ad
                reloadAd={adReload}
                targeting={{ refresh: adReload.toString() }}
                {...adParams.leaderboardModalParams}
              />
            )}
          </>
        )}
      </>
    </Modal>
  );
};

CarouselModalMobile.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired
  }).isRequired,
  /** Kind of device */
  device: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),
  /** Defines if the component data is loading */
  isLoading: PropTypes.bool,
  /** The listing of data */
  listing: listingPropTypes,
  url: PropTypes.string,
  /** the selected item in the carousel */
  selectedCarouselItem: PropTypes.number,
  adParams: PropTypes.object,
  adReload: PropTypes.number,
  /** show modal */
  show: PropTypes.bool,
  /** function that will be called when the modal is closed */
  hideModal: PropTypes.func,
  isManufacturer: PropTypes.bool,
  onCarouselContactButton: PropTypes.func
};

const CarouselModal = (props) => {
  const Component =
    props.device === 'desktop' ? CarouselModalDesktop : CarouselModalMobile;

  return <Component {...props} />;
};

CarouselModal.propTypes = {
  /** Kind of device */
  device: PropTypes.oneOf(['mobile', 'tablet', 'desktop']),
  isManufacturer: PropTypes.bool
};

export default injectIntl(CarouselModal);
